import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Switch, Select, Tag, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import adminApi from '../admin/config/adminApi';

const { Option } = Select;
const { TextArea } = Input;

const BusinessPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [features, setFeatures] = useState([]);
  const [currentFeature, setCurrentFeature] = useState('');

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/packages');
      setPackages(response.data.packages || []);
    } catch (error) {
      message.error('Failed to fetch packages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const showModal = (pkg = null) => {
    setEditingPackage(pkg);
    setIsModalVisible(true);
    if (pkg) {
      form.setFieldsValue({
        ...pkg,
        // Convert features array to string for textarea display
        features_text: pkg.features ? pkg.features.join('\n') : ''
      });
      setFeatures(pkg.features || []);
    } else {
      form.resetFields();
      setFeatures([]);
    }
  };

  const addFeature = () => {
    if (currentFeature.trim()) {
      setFeatures([...features, currentFeature.trim()]);
      setCurrentFeature('');
    }
  };

  const removeFeature = (index) => {
    const newFeatures = [...features];
    newFeatures.splice(index, 1);
    setFeatures(newFeatures);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Prepare the data for API
      const packageData = {
        ...values,
        features: features, // Use the features array
        is_popular: values.is_popular || false,
        has_trial: values.has_trial || false,
        trial_days: values.trial_days || null,
        count_warehouse: values.count_warehouse || 0
      };

      // Remove the temporary text field
      delete packageData.features_text;

      setLoading(true);
      if (editingPackage) {
        await adminApi.put(`/packages/${editingPackage.id}`, packageData);
        message.success('Package updated successfully');
      } else {
        await adminApi.post('/packages', packageData);
        message.success('Package added successfully');
      }
      setIsModalVisible(false);
      fetchPackages();
    } catch (error) {
      const msg = error.response?.data?.message || 'Action failed';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Are you sure?',
      content: 'This will permanently delete the package.',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await adminApi.delete(`/packages/${id}`);
          message.success('Package deleted');
          fetchPackages();
        } catch {
          message.error('Failed to delete package');
        }
      }
    });
  };

  const columns = [
    { title: 'Package Name', dataIndex: 'name', key: 'name' },
    { 
      title: 'Price (TZS)', 
      dataIndex: 'price', 
      key: 'price',
      render: (price) => `TZS ${parseFloat(price).toLocaleString()}`
    },
    { title: 'Duration (days)', dataIndex: 'duration_days', key: 'duration_days' },
    { title: 'Shops', dataIndex: 'shops_count', key: 'shops_count' },
    { title: 'Warehouses', dataIndex: 'count_warehouse', key: 'count_warehouse' },
    {
      title: 'Popular',
      dataIndex: 'is_popular',
      key: 'is_popular',
      render: (popular) => (
        <Tag color={popular ? 'green' : 'default'}>
          {popular ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Has Trial',
      dataIndex: 'has_trial',
      key: 'has_trial',
      render: (hasTrial) => (
        <Tag color={hasTrial ? 'blue' : 'default'}>
          {hasTrial ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, pkg) => (
        <Space>
          <Button onClick={() => showModal(pkg)} size="small">
            Edit
          </Button>
          <Button danger size="small" onClick={() => handleDelete(pkg.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 16 }}>
        <Button type="primary" onClick={() => showModal()} icon={<PlusOutlined />}>
          Add Package
        </Button>
      </div>

      <Table
        dataSource={packages.map(pkg => ({ ...pkg, key: pkg.id }))}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
        sticky
      />

      <Modal
        title={editingPackage ? 'Edit Package' : 'Add Package'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={loading}
        okText="Save"
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Package Name" rules={[{ required: true, message: 'Please enter package name' }]}>
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} />
          </Form.Item>

          <Form.Item name="price" label="Price (TZS)" rules={[{ required: true, message: 'Enter price' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="duration_days" label="Duration (Days)" rules={[{ required: true, message: 'Enter duration' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="shops_count" label="Shops Count" rules={[{ required: true, message: 'Enter shops count' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="count_warehouse" label="Warehouse Count">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="button_text" label="Button Text" rules={[{ required: true, message: 'Enter button text' }]}>
            <Input placeholder="e.g., Get Started, Contact Sales" />
          </Form.Item>

          <Form.Item label="Features">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={currentFeature}
                onChange={(e) => setCurrentFeature(e.target.value)}
                placeholder="Enter feature"
                onPressEnter={addFeature}
              />
              <Button type="primary" onClick={addFeature}>
                Add
              </Button>
            </Space.Compact>
            
            <div style={{ marginTop: 8 }}>
              {features.map((feature, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeFeature(index)}
                  style={{ marginBottom: 4 }}
                >
                  {feature}
                </Tag>
              ))}
            </div>
          </Form.Item>

          <Form.Item name="is_popular" label="Is Popular" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="has_trial" label="Has Trial" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="trial_days" label="Trial Days (if has trial)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BusinessPackages;